import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";

import ConfirmAction from "./ConfirmAction";
import { formatDateTime, useToolStyles } from "./styles";
import { adminToolsApi, isRunActive } from "../../utils/adminToolsApi";

const POLL_MS = 5000;

const CONFIRM_TEXT = {
  dry:
    "Asks AAU about every membership created since 2025-08-01 and counts what " +
    "would change. Nothing is saved. Takes about 25 minutes.",
  live:
    "Asks AAU about every membership created since 2025-08-01 and saves the " +
    "answer: lapsed or unknown memberships become FAILED, newly valid ones " +
    "SUCCESS. Takes about 25 minutes.",
};

const statusColor = (status) =>
  status === "COMPLETED"
    ? "primary"
    : status === "RUNNING"
    ? "secondary"
    : "default";

const AauReverificationTool = () => {
  const classes = useToolStyles();
  const [runs, setRuns] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const [confirm, setConfirm] = useState(null); // "dry" | "live" | null
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const page = await adminToolsApi.listAauRuns({ page: 0, size: 10 });
      const list = page?.runs || [];
      setRuns(list);
      setActiveRun(list.find(isRunActive) || null);
    } catch (error) {
      toast.error(error.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Poll the active run until it finishes, then refresh the history.
  const activeId = activeRun?.id;
  useEffect(() => {
    if (!activeId) return undefined;
    const timer = setInterval(async () => {
      try {
        const run = await adminToolsApi.getAauRun(activeId);
        if (isRunActive(run)) {
          setActiveRun(run);
          setRuns((list) => list.map((r) => (r.id === run.id ? run : r)));
        } else {
          toast.info(`AAU run #${run.id} ${run.status.toLowerCase()}`);
          load();
        }
      } catch (_) {
        // Keep polling; a blip should not stop the progress view.
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [activeId, load]);

  const start = async () => {
    setBusy(true);
    try {
      const run = await adminToolsApi.startAauRun(confirm === "dry");
      toast.success(`AAU run #${run.id} started`);
      setConfirm(null);
      await load();
    } catch (error) {
      toast.error(
        error.status === 409 ? `Not started: ${error.message}` : error.message,
      );
    } finally {
      setBusy(false);
    }
  };

  const progress =
    activeRun && activeRun.candidateCount > 0
      ? (100 * activeRun.processedCount) / activeRun.candidateCount
      : null;

  return (
    <Paper
      className={classes.section}
      component="section"
      aria-labelledby="aau-tool"
    >
      <Typography id="aau-tool" component="h2" className={classes.sectionTitle}>
        AAU membership re-verification
      </Typography>
      <Typography className={classes.sectionText}>
        Re-checks stored AAU memberships against AAU and records the answer.
        Runs by itself every Wednesday at 03:00 (New York) in production. A dry
        run asks AAU but changes nothing. If AAU stops answering, the run stops
        and leaves memberships as they were.
      </Typography>

      <Box className={classes.actions}>
        <Button
          variant="outlined"
          className={classes.touch}
          disabled={Boolean(activeRun)}
          onClick={() => setConfirm("dry")}
        >
          Start dry run
        </Button>
        <Button
          variant="contained"
          color="primary"
          className={classes.touch}
          disabled={Boolean(activeRun)}
          onClick={() => setConfirm("live")}
        >
          Start run
        </Button>
        <Button className={classes.touch} onClick={load}>
          Refresh
        </Button>
        {activeRun && (
          <Typography className={classes.muted}>
            Run #{activeRun.id} is {activeRun.status.toLowerCase()}
            {activeRun.dryRun ? " (dry run)" : ""}:{" "}
            {activeRun.processedCount.toLocaleString()} of{" "}
            {activeRun.candidateCount.toLocaleString()} checked
          </Typography>
        )}
      </Box>
      {activeRun && (
        <LinearProgress
          className={classes.progress}
          variant={progress === null ? "indeterminate" : "determinate"}
          value={progress ?? 0}
        />
      )}

      <TableContainer>
        <Table size="small" aria-label="Recent AAU runs">
          <TableHead>
            <TableRow>
              {[
                "Run",
                "Started",
                "Trigger",
                "Mode",
                "Status",
                "Checked",
                "Upgraded",
                "Downgraded",
                "Unchanged",
                "No answer",
                "Message",
              ].map((label) => (
                <TableCell key={label} className={classes.headerCell}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className={classes.cell}>
                  No runs yet.
                </TableCell>
              </TableRow>
            )}
            {runs.map((run) => (
              <TableRow key={run.id}>
                <TableCell className={classes.cell}>#{run.id}</TableCell>
                <TableCell className={classes.cell}>
                  {formatDateTime(run.startedAt || run.createdAt)}
                </TableCell>
                <TableCell className={classes.cell}>
                  {run.trigger === "SCHEDULED" ? "Schedule" : "Manual"}
                </TableCell>
                <TableCell className={classes.cell}>
                  {run.dryRun ? "Dry run" : "Live"}
                </TableCell>
                <TableCell className={classes.cell}>
                  <Chip
                    size="small"
                    label={run.status}
                    color={statusColor(run.status)}
                  />
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.number}`}>
                  {run.processedCount.toLocaleString()} /{" "}
                  {run.candidateCount.toLocaleString()}
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.number}`}>
                  {run.upgradedCount.toLocaleString()}
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.number}`}>
                  {run.downgradedCount.toLocaleString()}
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.number}`}>
                  {run.unchangedCount.toLocaleString()}
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.number}`}>
                  {run.unavailableCount.toLocaleString()}
                </TableCell>
                <TableCell className={classes.cell}>
                  {run.message || ""}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmAction
        open={confirm !== null}
        title={confirm === "dry" ? "Start a dry run?" : "Start a live run?"}
        text={CONFIRM_TEXT[confirm || "dry"]}
        confirmLabel={confirm === "dry" ? "Start dry run" : "Start run"}
        busy={busy}
        onConfirm={start}
        onCancel={() => setConfirm(null)}
      />
    </Paper>
  );
};

export default AauReverificationTool;
